import { NotificationVariant } from "../components/notifications/useNotifications";

export interface ValidationField {
  field: string;
  label: string;
  required?: boolean;
  minValue?: number;
  maxValue?: number;
  pattern?: RegExp;
  customValidation?: (value: any) => string | null;
}

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

export const validateFormFields = (
  data: Record<string, any>,
  fields: ValidationField[],
  addNotification?: (notification: {
    variant: typeof NotificationVariant[keyof typeof NotificationVariant];
    primaryText: string;
    secondaryText: string;
  }) => void
): boolean => {
  const errors: string[] = [];

  fields.forEach(({ field, label, required = true, minValue, maxValue, pattern, customValidation }) => {
    const value = data[field];
    
    // Check if required field is missing
    if (required && (!value || (typeof value === 'string' && value.trim() === '') || (typeof value === 'number' && value < 0))) {
      errors.push(`${label} is required`);
      return;
    }

    // Skip further validation if value is empty and not required
    if (!value && !required) {
      return;
    }

    // Check minimum value for numbers
    if (typeof value === 'number' && minValue !== undefined && value < minValue) {
      errors.push(`${label} must be at least ${minValue}`);
    }

    // Check maximum value for numbers
    if (typeof value === 'number' && maxValue !== undefined && value > maxValue) {
      errors.push(`${label} must be no more than ${maxValue}`);
    }

    // Check pattern for strings
    if (typeof value === 'string' && pattern && !pattern.test(value)) {
      errors.push(`${label} format is invalid`);
    }

    // Custom validation
    if (customValidation) {
      const customError = customValidation(value);
      if (customError) {
        errors.push(customError);
      }
    }
  });

  if (errors.length > 0 && addNotification) {
    addNotification({
      variant: NotificationVariant.error,
      primaryText: "Validation errors",
      secondaryText: errors.join(', ')
    });
  }

  return errors.length === 0;
}; 